<template>
  <div class="landing-page-root" :class="{'light-mode': !darkMode}">

    <!-- Theme Toggle -->
    <button class="lp-theme-toggle" @click="toggleTheme" :title="darkMode?'Switch to Light':'Switch to Dark'">
      <i :class="darkMode?'mdi mdi-white-balance-sunny':'mdi mdi-moon-waning-crescent'" style="font-size:18px;"></i>
    </button>

    <div id="layout-wrapper">

      <!-- NAVBAR -->
      <nav class="landing-nav" :class="{ 'nav-scrolled': isScrolled }">
        <div class="container d-flex align-items-center justify-content-between py-2">
          <a href="javascript:void(0)" @click="$router.push('/')" class="nav-brand">
            <img :src="$brand.logo" alt="Hospital Management System" style="height:42px;" />
          </a>
          <div class="d-none d-lg-flex align-items-center">
            <a href="#features" class="landing-nav-link" @click.prevent="scrollTo('features')">Modules</a>
            <a href="#how-it-works" class="landing-nav-link" @click.prevent="scrollTo('how-it-works')">How It Works</a>
            <a href="#ecosystem" class="landing-nav-link" @click.prevent="scrollTo('ecosystem')">For Your Team</a>
            <a href="#security" class="landing-nav-link" @click.prevent="scrollTo('security')">Security</a>
            <a href="#pricing" class="landing-nav-link" @click.prevent="scrollTo('pricing')">Pricing</a>
            <span class="landing-nav-divider"></span>
            <template v-if="isLoggedIn">
              <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="btn btn-sm btn-outline-primary me-2"><i class="mdi mdi-view-dashboard me-1"></i>Dashboard</a>
              <div class="landing-user-pill" @click="showUserDrop=!showUserDrop">
                <div class="landing-avatar">{{ userInitials }}</div>
                <span class="landing-user-name">{{ userName }}</span>
                <i class="mdi mdi-chevron-down landing-user-caret"></i>
              </div>
              <div v-if="showUserDrop" class="landing-user-drop">
                <a href="javascript:void(0)" @click="$router.push('/dashboard');showUserDrop=false" class="landing-drop-item"><i class="mdi mdi-view-dashboard me-2"></i>Dashboard</a>
                <a href="javascript:void(0)" @click="$router.push('/dashboard');showUserDrop=false" class="landing-drop-item"><i class="mdi mdi-account-cog me-2"></i>My Profile</a>
                <div class="landing-drop-divider"></div>
                <a href="javascript:void(0)" @click="logout" class="landing-drop-item landing-drop-danger"><i class="mdi mdi-logout me-2"></i>Sign Out</a>
              </div>
            </template>
            <template v-else>
              <a href="javascript:void(0)" @click="$router.push('/portal/login')" class="btn btn-sm btn-outline-primary me-2">Patient Portal</a>
              <a href="javascript:void(0)" @click="$router.push('/login')" class="btn btn-sm btn-outline-primary me-2">Staff Sign In</a>
              <a href="javascript:void(0)" @click="$router.push('/signup')" class="btn btn-sm btn-primary fw-bold">Request a Demo</a>
            </template>
          </div>
          <div class="d-flex align-items-center d-lg-none" style="gap:8px;">
            <button class="lp-theme-toggle-mobile" @click="toggleTheme" :title="darkMode?'Switch to Light':'Switch to Dark'">
              <i :class="darkMode?'mdi mdi-white-balance-sunny':'mdi mdi-moon-waning-crescent'" style="font-size:18px;"></i>
            </button>
            <button class="btn btn-link p-0 landing-burger-btn" @click="mobileMenuOpen = !mobileMenuOpen">
              <i class="mdi mdi-menu" style="font-size:28px;" v-if="!mobileMenuOpen"></i>
              <i class="mdi mdi-close" style="font-size:28px;" v-else></i>
            </button>
          </div>
        </div>
        <div v-if="mobileMenuOpen" class="landing-mobile-menu d-lg-none">
          <a href="#features" class="landing-mobile-link" @click.prevent="scrollTo('features'); mobileMenuOpen=false">Modules</a>
          <a href="#how-it-works" class="landing-mobile-link" @click.prevent="scrollTo('how-it-works'); mobileMenuOpen=false">How It Works</a>
          <a href="#ecosystem" class="landing-mobile-link" @click.prevent="scrollTo('ecosystem'); mobileMenuOpen=false">For Your Team</a>
          <a href="#security" class="landing-mobile-link" @click.prevent="scrollTo('security'); mobileMenuOpen=false">Security</a>
          <a href="#pricing" class="landing-mobile-link" @click.prevent="scrollTo('pricing'); mobileMenuOpen=false">Pricing</a>
          <div class="pt-3">
            <template v-if="isLoggedIn">
              <a href="javascript:void(0)" @click="$router.push('/dashboard');mobileMenuOpen=false" class="btn btn-outline-primary w-100 mb-2"><i class="mdi mdi-view-dashboard me-1"></i>Dashboard</a>
              <a href="javascript:void(0)" @click="logout" class="btn btn-outline-danger w-100"><i class="mdi mdi-logout me-1"></i>Sign Out</a>
            </template>
            <template v-else>
              <a href="javascript:void(0)" @click="$router.push('/login')" class="btn btn-outline-primary w-100 mb-2">Sign In</a>
              <a href="javascript:void(0)" @click="$router.push('/signup')" class="btn btn-primary fw-bold w-100">Request a Demo</a>
            </template>
          </div>
        </div>
      </nav>

      <!-- HERO -->
      <section class="landing-hero">
        <div class="container">
          <div class="row align-items-center" style="min-height:calc(100vh - 80px); padding-top:80px;">
            <div class="col-lg-7">
              <div class="mb-3">
                <span class="badge landing-hero-badge px-3 py-2"><i class="mdi mdi-shield-check me-1"></i> Built for multi-branch hospital networks</span>
              </div>
              <h1 class="landing-hero-title">Run Your Hospital Network <br/><span class="landing-hero-accent">on One Platform</span></h1>
              <p class="landing-hero-subtitle mt-3">A modern, AI-assisted Hospital Management System covering patient records, wards &amp; beds, pharmacy, laboratory, billing, queueing and disease registries — designed for multi-branch healthcare providers.</p>
              <div class="mt-4">
                <a href="javascript:void(0)" @click="$router.push('/signup')" class="btn btn-primary fw-bold btn-lg me-2 mb-2"><i class="mdi mdi-rocket-launch me-1"></i> Request a Demo</a>
                <a href="#how-it-works" class="btn btn-outline-primary btn-lg mb-2" @click.prevent="scrollTo('how-it-works')"><i class="mdi mdi-play-circle me-1"></i> See How It Works</a>
              </div>
              <div class="d-flex align-items-center mt-5 flex-wrap">
                <div class="text-center px-3"><div class="fw-bold fs-4 landing-stat-num">60+</div><small class="landing-stat-lbl">Modules</small></div>
                <div class="landing-stat-divider"></div>
                <div class="text-center px-3"><div class="fw-bold fs-4 landing-stat-num">Multi</div><small class="landing-stat-lbl">Branch &amp; Tenant</small></div>
                <div class="landing-stat-divider"></div>
                <div class="text-center px-3"><div class="fw-bold fs-4 landing-stat-num">AI</div><small class="landing-stat-lbl">Clinical Assistant</small></div>
                <div class="landing-stat-divider"></div>
                <div class="text-center px-3"><div class="fw-bold fs-4 landing-stat-num">24/7</div><small class="landing-stat-lbl">Cloud Uptime</small></div>
              </div>
            </div>
            <div class="col-lg-5 d-none d-lg-block">
              <div class="text-center">
                <div class="landing-phone-mockup">
                  <div class="landing-phone-screen">
                    <div class="text-center pb-2 mb-2 landing-phone-header">
                      <img :src="$brand.logo" alt="HMS" style="height:30px;opacity:.95;" />
                    </div>
                    <div class="landing-phone-row d-flex align-items-center py-2" v-for="(p, pi) in phonePreview" :key="'pp-'+pi">
                      <div class="landing-phone-icon"><i :class="p.icon" style="font-size:28px;"></i></div>
                      <div class="ms-2 flex-grow-1">
                        <div class="fw-semibold landing-phone-title">{{ p.title }}</div>
                        <div class="landing-phone-meta"><i :class="p.metaIcon"></i> {{ p.meta }}</div>
                      </div>
                      <span class="landing-phone-pill" :class="p.pillClass">{{ p.pill }}</span>
                    </div>
                    <div class="text-center mt-3">
                      <span class="btn btn-sm btn-primary fw-bold px-4" @click="$router.push('/login')">Open Dashboard</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- TRUSTED / INTEGRATED -->
      <section class="py-4 landing-band">
        <div class="container">
          <p class="text-center landing-band-label mb-3">DESIGNED FOR THE TOOLS HOSPITALS ALREADY USE</p>
          <div class="d-flex justify-content-center align-items-center flex-wrap landing-band-row">
            <div class="text-center landing-band-item"><i class="mdi mdi-cellphone-wireless d-block"></i><small class="fw-semibold">M-Pesa</small></div>
            <div class="text-center landing-band-item"><i class="mdi mdi-credit-card-outline d-block"></i><small class="fw-semibold">Card &amp; Bank</small></div>
            <div class="text-center landing-band-item"><i class="mdi mdi-shield-account d-block"></i><small class="fw-semibold">SHA</small></div>
            <div class="text-center landing-band-item"><i class="mdi mdi-database-export d-block"></i><small class="fw-semibold">DHIS2 / KHIS</small></div>
            <div class="text-center landing-band-item"><i class="mdi mdi-robot-outline d-block"></i><small class="fw-semibold">Claude AI</small></div>
          </div>
        </div>
      </section>

      <!-- FEATURES / MODULES -->
      <section id="features" class="py-5 landing-section-2">
        <div class="container">
          <div class="text-center mb-5">
            <span class="badge landing-eyebrow px-3 py-2">THE PLATFORM</span>
            <h2 class="fw-bold mt-3 landing-section-title">One System. Every Department.</h2>
            <p class="landing-section-sub mx-auto">From the front desk to the ward, the lab to the pharmacy — every clinical and administrative workflow in one place.</p>
          </div>
          <div class="row g-4">
            <div class="col-lg-4 col-md-6" v-for="(f, fi) in features" :key="fi">
              <div class="card h-100 landing-feature-card landing-feature-card-link" role="button" tabindex="0" @click="openModule(f)" @keyup.enter="openModule(f)">
                <div class="card-body p-4">
                  <div class="landing-feature-icon mb-3" :style="'background:' + f.bgColor"><i :class="f.icon + ' font-size-24'" :style="'color:' + f.iconColor"></i></div>
                  <h5 class="fw-bold mb-2 landing-feature-title">{{ f.title }}</h5>
                  <p class="mb-3 landing-feature-desc">{{ f.desc }}</p>
                  <div class="mb-3"><span class="landing-feature-tag" v-for="(tag, ti) in f.tags" :key="ti">{{ tag }}</span></div>
                  <span class="landing-feature-cta">Open module <i class="mdi mdi-arrow-right"></i></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- HOW IT WORKS -->
      <section id="how-it-works" class="py-5 landing-section-1">
        <div class="container">
          <div class="text-center mb-5">
            <span class="badge landing-eyebrow px-3 py-2">SIMPLE PROCESS</span>
            <h2 class="fw-bold mt-3 landing-section-title">How HMS Works</h2>
            <p class="landing-section-sub">Onboard your hospital, set up branches, register staff and patients — start running in days, not months.</p>
          </div>
          <div class="mb-5" v-for="(flow, fi) in flows" :key="fi">
            <h4 class="mb-4 landing-flow-title"><i :class="flow.icon" class="me-2"></i> {{ flow.title }}</h4>
            <div class="row g-4">
              <div class="col-lg-3 col-md-6" v-for="(step, si) in flow.steps" :key="si">
                <div class="card h-100 landing-step-card">
                  <div class="card-body p-4 text-center">
                    <div class="landing-step-number">{{ si + 1 }}</div>
                    <div class="my-3 landing-step-icon"><i :class="step.icon"></i></div>
                    <h6 class="fw-bold mb-2 landing-step-title">{{ step.title }}</h6>
                    <p class="mb-0 landing-step-desc">{{ step.desc }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ECOSYSTEM / FOR YOUR TEAM -->
      <section id="ecosystem" class="py-5 landing-section-2">
        <div class="container">
          <div class="text-center mb-5">
            <span class="badge landing-eyebrow px-3 py-2">FOR EVERY ROLE</span>
            <h2 class="fw-bold mt-3 landing-section-title">Built for Your Whole Team</h2>
            <p class="landing-section-sub mx-auto">Role-based dashboards and workflows for everyone who runs your hospital.</p>
          </div>
          <div class="row g-4 justify-content-center">
            <div class="col-lg-4 col-md-6" v-for="(eco, ei) in ecoCards" :key="ei">
              <div class="card h-100 landing-eco-card">
                <div class="card-body p-4">
                  <div class="landing-eco-icon"><i :class="eco.icon"></i></div>
                  <h5 class="fw-bold mb-2 landing-eco-title">{{ eco.title }}</h5>
                  <p class="mb-3 landing-eco-desc">{{ eco.desc }}</p>
                  <ul class="list-unstyled mb-0">
                    <li v-for="(item, ii) in eco.items" :key="ii" class="d-flex align-items-center py-1 landing-eco-item">
                      <i class="mdi mdi-check landing-eco-check"></i> {{ item }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- SECURITY & COMPLIANCE -->
      <section id="security" class="py-5 landing-section-1">
        <div class="container">
          <div class="row align-items-center">
            <div class="col-lg-6">
              <span class="badge landing-eyebrow px-3 py-2">SECURE BY DESIGN</span>
              <h2 class="fw-bold mt-3 landing-section-title">Patient Data Stays Safe</h2>
              <p class="mt-3 mb-4 landing-trust-lead">Every action is audited. Every record is multi-tenant isolated. Bearer-token authentication, role-based access control, soft-delete recovery and a full audit trail across every clinical and financial event.</p>
              <div class="d-flex flex-column landing-trust-list">
                <div class="d-flex align-items-center landing-trust-item" v-for="(tf, tfi) in trustFeatures" :key="tfi">
                  <i class="mdi mdi-check-circle font-size-20 landing-trust-icon"></i>
                  <span>{{ tf }}</span>
                </div>
              </div>
            </div>
            <div class="col-lg-6 mt-4 mt-lg-0">
              <div class="card landing-trust-card">
                <div class="card-body p-4">
                  <div class="d-flex align-items-center justify-content-center mb-4">
                    <div class="landing-flow-icon" style="background:rgba(80,165,241,.15);color:#50a5f1;"><i class="mdi mdi-account-key"></i></div>
                    <div class="landing-flow-line"></div>
                    <div class="landing-flow-icon landing-flow-mid"><i class="mdi mdi-shield-lock"></i></div>
                    <div class="landing-flow-line"></div>
                    <div class="landing-flow-icon" style="background:rgba(52,195,143,.15);color:#34c38f;"><i class="mdi mdi-database-check"></i></div>
                  </div>
                  <div class="d-flex justify-content-between mb-4 px-3 landing-flow-labels">
                    <span>Authenticate</span><span>Authorise</span><span>Audit</span>
                  </div>
                  <div class="d-flex flex-column px-3 landing-tl-list">
                    <div class="d-flex align-items-center landing-tl-row" v-for="(tl, tli) in securityTimeline" :key="tli">
                      <div class="landing-tl-dot" :class="{ active: tl.active }"></div>
                      <span>{{ tl.label }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- PRICING -->
      <section id="pricing" class="py-5 landing-section-2">
        <div class="container">
          <div class="text-center mb-5">
            <span class="badge landing-eyebrow px-3 py-2">PRICING</span>
            <h2 class="fw-bold mt-3 landing-section-title">Plans That Scale With You</h2>
            <p class="landing-section-sub">From single-branch clinics to enterprise hospital networks. Talk to us — we tailor pricing to your facility size.</p>
          </div>
          <div class="row g-4 justify-content-center">
            <div class="col-lg-4 col-md-6" v-for="(plan, pi) in pricingPlans" :key="pi">
              <div class="card h-100 landing-pricing-card" :class="{ 'landing-pricing-popular': plan.popular }">
                <div v-if="plan.popular" class="landing-popular-badge">MOST POPULAR</div>
                <div class="card-body p-4 text-center">
                  <div class="fw-bold mb-1 landing-plan-tier">{{ plan.tier }}</div>
                  <div class="fw-bold landing-plan-price">{{ plan.price }}<span class="landing-plan-period">{{ plan.period }}</span></div>
                  <ul class="list-unstyled text-start my-4">
                    <li v-for="(feat, ffi) in plan.features" :key="ffi" class="d-flex align-items-center py-2 landing-plan-row" :class="feat.disabled ? 'landing-plan-row-off' : ''">
                      <i :class="feat.disabled ? 'mdi mdi-close landing-plan-icon-off' : 'mdi mdi-check landing-plan-icon-on'"></i>
                      {{ feat.text }}
                    </li>
                  </ul>
                  <div class="landing-plan-note mb-3">{{ plan.note }}</div>
                  <a href="javascript:void(0)" @click="$router.push('/signup')" class="btn w-100" :class="plan.popular ? 'btn-primary fw-bold' : 'btn-outline-primary'">{{ plan.cta }}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- CTA -->
      <section class="py-5 landing-cta">
        <div class="container text-center py-4">
          <h2 class="fw-bold landing-section-title">Ready to Modernise Your Hospital?</h2>
          <p class="mt-2 mb-4 landing-cta-sub">Talk to us about onboarding your hospital network onto a single, AI-assisted platform.</p>
          <div>
            <a href="javascript:void(0)" @click="$router.push('/signup')" class="btn btn-primary fw-bold btn-lg me-2 mb-2"><i class="mdi mdi-rocket-launch me-1"></i> Request a Demo</a>
            <a href="javascript:void(0)" @click="$router.push('/login')" class="btn btn-outline-primary btn-lg mb-2"><i class="mdi mdi-login me-1"></i> Sign In</a>
          </div>
          <p class="landing-cta-note mt-3">Free pilot for qualifying hospitals. We onboard with you, end to end.</p>
        </div>
      </section>

      <!-- FOOTER -->
      <footer class="landing-footer">
        <div class="container">
          <div class="row g-4">
            <div class="col-lg-4">
              <img :src="$brand.logo" alt="Hospital Management System" class="landing-footer-logo mb-3" />
              <p class="landing-footer-desc">A next-generation Hospital Management System for multi-branch healthcare networks. Patient records, wards, pharmacy, lab, billing, queues, registries and AI clinical assistance — one platform.</p>
              <div class="d-flex landing-footer-social">
                <a href="#" class="landing-social-icon"><i class="mdi mdi-linkedin"></i></a>
                <a href="#" class="landing-social-icon"><i class="mdi mdi-twitter"></i></a>
                <a href="#" class="landing-social-icon"><i class="mdi mdi-facebook"></i></a>
                <a href="#" class="landing-social-icon"><i class="mdi mdi-youtube"></i></a>
                <a href="#" class="landing-social-icon"><i class="mdi mdi-whatsapp"></i></a>
              </div>
            </div>
            <div class="col-lg-2 col-6" v-for="(col, ci) in footerColumns" :key="ci">
              <h6 class="landing-footer-heading">{{ col.heading }}</h6>
              <ul class="list-unstyled">
                <li v-for="(link, li) in col.links" :key="li" class="mb-2">
                  <a href="javascript:void(0)" @click="goLink(link.url)" class="landing-footer-link">{{ link.text }}</a>
                </li>
              </ul>
            </div>
          </div>
          <div class="text-center py-3 mt-4 landing-footer-bottom">
            <p class="mb-0 landing-footer-copy">&copy; {{ currentYear }} Hospital Management System. All rights reserved.</p>
          </div>
        </div>
      </footer>

    </div>
    <div class="rightbar-overlay"></div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: "LandingPage",
  data() {
    return {
      darkMode: (document.documentElement.getAttribute('data-theme') || localStorage.getItem('hms_theme') || 'light') !== 'light',
      themeObserver: null,
      isScrolled: false,
      mobileMenuOpen: false,
      showUserDrop: false,
      isLoggedIn: false,
      userName: '',
      userInitials: '',

      phonePreview: [
        { icon: 'mdi mdi-account-injury', title: 'Mary K. — Bed B-12',  meta: 'Ward: Maternity',     metaIcon: 'mdi mdi-bed',           pill: 'Stable',  pillClass: 'landing-phone-pill-ok' },
        { icon: 'mdi mdi-needle',          title: 'Lab Order #LAB-0042', meta: 'CBC + UECs',          metaIcon: 'mdi mdi-flask-outline', pill: 'STAT',    pillClass: 'landing-phone-pill-warn' },
        { icon: 'mdi mdi-pill',            title: 'RX-008 Dispensed',     meta: 'Amoxicillin 500mg',   metaIcon: 'mdi mdi-pill-multiple', pill: 'Done',    pillClass: 'landing-phone-pill-ok' },
        { icon: 'mdi mdi-cash-multiple',   title: 'INV-1207 Paid',        meta: 'M-Pesa · KES 4,500',  metaIcon: 'mdi mdi-cash',          pill: 'Paid',    pillClass: 'landing-phone-pill-ok' }
      ],

      features: [
        { title: 'Patient Records & EMR',     desc: 'Unified patient files — demographics, vitals, diagnoses, visits, allergies, immunisations and consultation notes.',                                          icon: 'mdi mdi-account-heart',     bgColor: 'rgba(26,127,232,.12)', iconColor: '#1a7fe8', tags: ['EMR', 'Vitals', 'ICD-10'], link: '/patients' },
        { title: 'Wards, Beds & Admissions',  desc: 'Live bed map with occupancy, transfers, cleaning SLA, and full admission-to-discharge workflow across wards.',                                                icon: 'mdi mdi-bed',               bgColor: 'rgba(52,195,143,.12)', iconColor: '#34c38f', tags: ['Bed Map', 'Transfers', 'Discharge'], link: '/wards' },
        { title: 'Pharmacy & Stock',          desc: 'Drug formulary, stock control with expiry alerts, prescription dispensing, narcotic register and stock movements.',                                          icon: 'mdi mdi-pill',              bgColor: 'rgba(241,180,76,.12)', iconColor: '#f1b44c', tags: ['Stock', 'Dispensing', 'Expiry Alerts'], link: '/pharmacy' },
        { title: 'Laboratory & Diagnostics',  desc: 'Lab and radiology requests, sample tracking, result entry, verification workflow and result reporting.',                                                     icon: 'mdi mdi-flask',             bgColor: 'rgba(80,165,241,.12)', iconColor: '#50a5f1', tags: ['Lab Orders', 'Radiology', 'Verify'], link: '/lab' },
        { title: 'Billing, Invoicing & Payments', desc: 'Service catalogue, invoices, M-Pesa / card / bank / insurance / corporate payments, and full insurance claims workflow.',                                 icon: 'mdi mdi-cash-multiple',     bgColor: 'rgba(245,130,31,.14)', iconColor: '#F5821F', tags: ['M-Pesa', 'SHA', 'Claims'], link: '/billing' },
        { title: 'Queue & Ticketing',         desc: 'Issue tickets per department, priority queueing, counter calls, public display screen with voice announcements.',                                            icon: 'mdi mdi-ticket-confirmation', bgColor: 'rgba(155,89,182,.12)', iconColor: '#9b59b6', tags: ['Tickets', 'Display', 'Recall'], link: '/queue' },
        { title: 'AI Clinical Assistant',     desc: 'Differential diagnoses, SOAP / discharge / referral note drafting, ICD code suggestions and lab interpretation — physician-reviewed.',                       icon: 'mdi mdi-robot-outline',     bgColor: 'rgba(26,188,156,.12)', iconColor: '#1abc9c', tags: ['Diagnosis', 'Notes', 'ICD-10'], link: '/ai' },
        { title: 'Disease Registries',        desc: 'HIV / ART, TB & contact tracing, MCH, cancer, diabetes, dialysis, mental health and nutrition — built-in, not bolt-on.',                                     icon: 'mdi mdi-clipboard-pulse',   bgColor: 'rgba(231,76,60,.12)',  iconColor: '#e74c3c', tags: ['HIV', 'TB', 'MCH'], link: '/registry' },
        { title: 'Multi-branch & Referrals',  desc: 'Run multiple branches under one tenant. Inter-branch referrals, stock transfers and consolidated reporting.',                                                icon: 'mdi mdi-hospital-building', bgColor: 'rgba(232,67,147,.12)', iconColor: '#e84393', tags: ['Multi-branch', 'Referrals', 'Reports'], link: '/referrals' }
      ],

      flows: [
        { title: 'Setting Up Your Hospital', icon: 'mdi mdi-hospital-building', steps: [
            { icon: 'mdi mdi-domain-plus',      title: 'Onboard Hospital',  desc: 'Register your hospital tenant, brand it, and configure subscription plan and locale.' },
            { icon: 'mdi mdi-source-branch',    title: 'Add Branches',      desc: 'Spin up one or many branches — each with its own wards, beds, staff and stock.' },
            { icon: 'mdi mdi-account-group',    title: 'Staff & Roles',     desc: 'Invite doctors, nurses, pharmacists, lab and admin staff with role-based access.' },
            { icon: 'mdi mdi-rocket-launch',    title: 'Go Live',           desc: 'Start registering patients, issuing tickets and operating the wards from day one.' }
          ]},
        { title: 'A Day In Operations', icon: 'mdi mdi-stethoscope', steps: [
            { icon: 'mdi mdi-account-plus',     title: 'Register & Triage', desc: 'Register patient, capture vitals, assign triage level, issue queue ticket.' },
            { icon: 'mdi mdi-stethoscope',      title: 'Consult & Order',   desc: 'Doctor records SOAP notes, requests labs / radiology, prescribes drugs, admits if needed.' },
            { icon: 'mdi mdi-flask-outline',    title: 'Investigate & Treat', desc: 'Pharmacy dispenses. Lab and radiology process and verify results. Bedside vitals captured live.' },
            { icon: 'mdi mdi-cash-check',       title: 'Bill & Discharge',  desc: 'Generate invoice, accept M-Pesa / card / insurance payment, discharge with summary.' }
          ]}
      ],

      ecoCards: [
        { icon: 'mdi mdi-doctor',              title: 'Doctors & Consultants', desc: 'Fast EMR access, AI-assisted notes, diagnosis support, prescription writer and consultation history at a glance.', items: ['Patient timeline & history', 'AI SOAP / discharge notes', 'ICD-10 code suggestions', 'Prescription & order writer'] },
        { icon: 'mdi mdi-medical-bag',         title: 'Nurses',                desc: 'Bedside vitals, drug administration, ward rounds, bed cleaning SLAs and care escalations — all from a tablet.',         items: ['Vitals & pain scoring', 'Bed map & transfers', 'Bed cleaning workflow', 'Ward round summary'] },
        { icon: 'mdi mdi-pill-multiple',       title: 'Pharmacists',           desc: 'Drug formulary, stock alerts, batch and expiry tracking, dispensing workflow with controlled-drug register.',           items: ['Formulary & stock', 'Expiry alerts', 'Dispensing workflow', 'Narcotic register'] },
        { icon: 'mdi mdi-flask',               title: 'Lab & Radiology',       desc: 'Sample tracking, result entry and verification, radiology reporting with image attachments.',                            items: ['Sample tracking', 'Result entry & verify', 'Radiology reporting', 'Critical value alerts'] },
        { icon: 'mdi mdi-cash-register',       title: 'Billing & Reception',   desc: 'Front desk, queue management, invoicing, M-Pesa / card / insurance / corporate payments and receipts.',                  items: ['Patient registration', 'Queue & tickets', 'Invoices & receipts', 'Insurance claims'] },
        { icon: 'mdi mdi-shield-account',      title: 'Hospital Admin',        desc: 'Multi-branch oversight, role &amp; permission control, audit trail, system config and consolidated reporting.',          items: ['Multi-branch reports', 'Roles & permissions', 'Audit trail', 'System configuration'] }
      ],

      trustFeatures: [
        'Multi-tenant isolation — every record scoped to hospital and branch',
        'Bearer-token authentication with role-based access control',
        'Full audit trail on every create, update and soft-delete',
        'Soft-delete only — no record is ever permanently destroyed',
        'AI outputs explicitly tagged for physician review — never autonomous',
        'API rate limiting and security headers on every request'
      ],

      securityTimeline: [
        { label: 'User signs in with Bearer token',      active: true },
        { label: 'Role checked against module access',   active: true },
        { label: 'Tenant + branch filter on every query',active: true },
        { label: 'Action recorded to audit log',         active: false },
        { label: 'Soft-delete keeps full history',       active: false }
      ],

      pricingPlans: [
        { tier: 'Basic',      price: 'Talk to us', period: '',           popular: false, note: 'For single-branch clinics getting started', cta: 'Request Pricing', features: [
            { text: 'Single branch' }, { text: 'Patients, EMR & visits' }, { text: 'Pharmacy & basic stock' }, { text: 'Billing &amp; M-Pesa' }, { text: 'Queue display' }, { text: 'AI clinical assistant', disabled: true }, { text: 'Disease registries', disabled: true }
          ]},
        { tier: 'Standard',   price: 'Talk to us', period: '',           popular: true,  note: 'For mid-size hospitals with multi-branch ops', cta: 'Book a Demo',     features: [
            { text: 'Up to 5 branches' }, { text: 'Full EMR + lab + radiology' }, { text: 'Pharmacy w/ controlled drugs' }, { text: 'Insurance &amp; claims' }, { text: 'Inter-branch referrals' }, { text: 'AI clinical assistant' }, { text: 'Disease registries', disabled: true }
          ]},
        { tier: 'Enterprise', price: 'Talk to us', period: '',           popular: false, note: 'For hospital chains and group networks',     cta: 'Contact Sales',  features: [
            { text: 'Unlimited branches' }, { text: 'All Standard features' }, { text: 'All disease registries' }, { text: 'Funeral Home &amp; theatre' }, { text: 'DHIS2 / KHIS reporting' }, { text: 'Full AI suite + audit' }, { text: 'Custom integrations' }
          ]}
      ],

      footerColumns: [
        { heading: 'Modules',     links: [
            { text: 'Patients & EMR',     url: '/dashboard' },
            { text: 'Wards & Beds',       url: '/dashboard' },
            { text: 'Pharmacy',           url: '/dashboard' },
            { text: 'Laboratory',         url: '/dashboard' },
            { text: 'Billing',            url: '/dashboard' }
          ]},
        { heading: 'Specialties', links: [
            { text: 'HIV / ART',          url: '/dashboard' },
            { text: 'TB Registry',        url: '/dashboard' },
            { text: 'Maternal & Child',   url: '/dashboard' },
            { text: 'Cancer',             url: '/dashboard' },
            { text: 'Mental Health',      url: '/dashboard' }
          ]},
        { heading: 'Platform',    links: [
            { text: 'AI Assistant',       url: '/dashboard' },
            { text: 'Multi-branch',       url: '/dashboard' },
            { text: 'Reports',            url: '/dashboard' },
            { text: 'Queue Display',      url: '/queue-display' },
            { text: 'M-Pesa Integration', url: '/dashboard' }
          ]},
        { heading: 'Company',     links: [
            { text: 'About',              url: '/' },
            { text: 'Pricing',            url: '/' },
            { text: 'Privacy Policy',     url: '/' },
            { text: 'Terms of Service',   url: '/' },
            { text: 'Help Centre',        url: '/' }
          ]}
      ]
    };
  },
  computed: {
    currentYear() { return new Date().getFullYear(); }
  },
  mounted() {
    this.syncThemeState();
    document.documentElement.setAttribute('data-theme', this.darkMode ? 'dark' : 'light');
    this.setupThemeObserver();
    window.addEventListener('scroll', this.handleScroll);
    this.checkLoginState();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('storage', this.syncThemeState);
    if (this.themeObserver) {
      this.themeObserver.disconnect();
      this.themeObserver = null;
    }
  },
  methods: {
    syncThemeState() {
      const docTheme = document.documentElement.getAttribute('data-theme');
      const storedTheme = localStorage.getItem('hms_theme');
      const resolvedTheme = docTheme || storedTheme || 'light';
      this.darkMode = resolvedTheme !== 'light';
    },

    setupThemeObserver() {
      this.themeObserver = new MutationObserver(() => {
        this.syncThemeState();
      });
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme']
      });
      window.addEventListener('storage', this.syncThemeState);
    },

    toggleTheme() {
      this.darkMode = !this.darkMode;
      const theme = this.darkMode ? 'dark' : 'light';
      localStorage.setItem('hms_theme', theme);
      document.documentElement.setAttribute('data-theme', theme);
    },

    handleScroll() { this.isScrolled = window.scrollY > 50; },
    scrollTo(id) { var el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); },
    goLink(url) { if (url) this.$router.push(url); },
    openModule(f) { if (f && f.link) this.$router.push(f.link); },

    checkLoginState() {
      var loggedIn = sessionStorage.getItem('isLoggedIn');
      if (loggedIn) {
        this.isLoggedIn = true;
        var name = sessionStorage.getItem('userName') || '';
        this.userName = name;
        var parts = name.split(' ');
        this.userInitials = parts.map(function(p) { return p.charAt(0).toUpperCase(); }).join('').substring(0, 2);
      }
    },

    logout() {
      sessionStorage.clear();
      localStorage.removeItem('authToken');
      this.isLoggedIn = false;
      this.userName = '';
      this.userInitials = '';
      this.showUserDrop = false;
      this.mobileMenuOpen = false;
      this.$router.push('/');
    }
  }
};
</script>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:          #f0f0f0;
  --hms-s:          #a0a0a0;
  --hms-m:          #808080;
  --hms-bg1:        #141414;
  --hms-bg2:        #0a0a0a;
  --hms-bg3:        #1e1e1e;
  --hms-bg4:        #0f0f0f;
  --hms-br:         #2a2a2a;
  --hms-input-br:   #333333;
  --hms-placeholder:#4a4a4a;
  --hms-primary:    #1a7fe8;
  --hms-primary-lt: rgba(26,127,232,0.12);
  --hms-accent:     #F5821F;
  --hms-success:    #28a745;
  --hms-danger:     #dc3545;
  --hms-warning:    #ffc107;
  --hms-info:       #17a2b8;
}
html[data-theme="light"] {
  --hms-h:          #1a1a1a;
  --hms-s:          #555555;
  --hms-m:          #777777;
  --hms-bg1:        #ffffff;
  --hms-bg2:        #f4f6f9;
  --hms-bg3:        #eef1f5;
  --hms-bg4:        #ffffff;
  --hms-br:         #dee2e8;
  --hms-input-br:   #ced4da;
  --hms-placeholder:#aaaaaa;
  --hms-primary:    #0047AB;
  --hms-primary-lt: rgba(0,71,171,0.08);
  --hms-accent:     #F5821F;
  --hms-success:    #28a745;
  --hms-danger:     #dc3545;
  --hms-warning:    #ffc107;
  --hms-info:       #17a2b8;
}
html[data-theme="light"] body,
html[data-theme="dark"] body {
  background: var(--hms-bg2) !important;
  color: var(--hms-h) !important;
}
</style>

<style scoped>
/* ---- ROOT ---- */
.landing-page-root { background: var(--hms-bg2); color: var(--hms-h); min-height: 100vh; }

/* ---- NAVBAR ---- */
.landing-nav { position:fixed; top:0; left:0; right:0; z-index:1050; padding:8px 0; transition:all .3s; background:transparent; }
.landing-nav.nav-scrolled { background: var(--hms-bg1); backdrop-filter: blur(20px); border-bottom:1px solid var(--hms-br); }
.landing-nav-link { color: var(--hms-m) !important; text-decoration:none; padding:8px 16px; font-size:14px; font-weight:500; }
.landing-nav-link:hover { color: var(--hms-primary) !important; }
.landing-nav-divider { width:1px; height:24px; background: var(--hms-br); margin:0 12px; display:inline-block; }
.landing-burger-btn { color: var(--hms-h); }

.landing-user-pill { display:flex; align-items:center; gap:8px; cursor:pointer; padding:4px 10px; border-radius:20px; border:1px solid var(--hms-br); position:relative; transition:all .2s; }
.landing-user-pill:hover { border-color: var(--hms-primary); }
.landing-avatar { width:28px; height:28px; border-radius:50%; background: var(--hms-primary-lt); color: var(--hms-primary); display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; }
.landing-user-name { color: var(--hms-h); font-size:13px; }
.landing-user-caret { color: var(--hms-s); font-size:14px; }
.landing-user-drop { position:absolute; top:60px; right:20px; background: var(--hms-bg1); border:1px solid var(--hms-br); border-radius:10px; padding:8px 0; min-width:200px; z-index:200; box-shadow:0 8px 30px rgba(0,0,0,.20); }
.landing-drop-item { display:flex; align-items:center; padding:8px 16px; color: var(--hms-m); text-decoration:none; font-size:13px; transition:all .2s; }
.landing-drop-item:hover { background: var(--hms-primary-lt); color: var(--hms-h); }
.landing-drop-divider { border-top:1px solid var(--hms-br); margin:6px 0; }
.landing-drop-danger { color: var(--hms-danger) !important; }
.landing-mobile-menu { background: var(--hms-bg1); border-top:1px solid var(--hms-br); padding:16px 24px; }
.landing-mobile-link { display:block; color: var(--hms-m) !important; text-decoration:none; padding:12px 0; border-bottom:1px solid var(--hms-br); font-size:15px; }
.landing-mobile-link:hover { color: var(--hms-primary) !important; }

/* ---- HERO ---- */
.landing-hero { background: linear-gradient(135deg, var(--hms-bg2) 0%, var(--hms-bg3) 50%, var(--hms-bg2) 100%); position:relative; overflow:hidden; }
.landing-hero::before { content:''; position:absolute; top:-50%; right:-20%; width:800px; height:800px; background: radial-gradient(circle, var(--hms-primary-lt) 0%, transparent 70%); border-radius:50%; }
.landing-hero-badge { background: var(--hms-primary-lt); color: var(--hms-primary); border:1px solid var(--hms-primary-lt); }
.landing-hero-title { font-size:52px; font-weight:800; line-height:1.1; color: var(--hms-h); }
.landing-hero-accent { color: var(--hms-primary); }
.landing-hero-subtitle { font-size:18px; line-height:1.7; color: var(--hms-m); max-width:620px; }
.landing-stat-num { color: var(--hms-primary); }
.landing-stat-lbl { color: var(--hms-s); }
.landing-stat-divider { width:1px; height:40px; background: var(--hms-br); }

/* ---- PHONE MOCKUP ---- */
.landing-phone-mockup { display:inline-block; width:300px; background: var(--hms-bg3); border-radius:28px; border:2px solid var(--hms-br); padding:16px; box-shadow:0 40px 80px rgba(0,0,0,.25); }
.landing-phone-screen { background: var(--hms-bg1); border-radius:18px; padding:16px; }
.landing-phone-icon { width:46px; height:46px; background: var(--hms-primary-lt); color: var(--hms-primary); border-radius:10px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.landing-phone-header { border-bottom:1px solid var(--hms-br); }
.landing-phone-row { border-bottom:1px solid var(--hms-br); }
.landing-phone-row:last-child { border-bottom: none; }
.landing-phone-title { font-size:13px; color: var(--hms-h); }
.landing-phone-meta { font-size:11px; color: var(--hms-s); }
.landing-phone-pill { font-size:10px; font-weight:700; padding:3px 8px; border-radius:50px; }
.landing-phone-pill-ok   { background: rgba(40,167,69,.15);  color: var(--hms-success); }
.landing-phone-pill-warn { background: rgba(245,130,31,.18); color: var(--hms-accent);  }

/* ---- BAND ---- */
.landing-band { background: var(--hms-bg2); border-bottom:1px solid var(--hms-br); }
.landing-band-label { color: var(--hms-s); font-size:12px; letter-spacing:2px; font-weight:600; }
.landing-band-row { gap:48px; }
.landing-band-item { color: var(--hms-s); }
.landing-band-item i { font-size:32px; color: var(--hms-primary); }

/* ---- SECTIONS ---- */
.landing-section-1 { background: var(--hms-bg1); }
.landing-section-2 { background: var(--hms-bg2); }
.landing-section-title { font-size:36px; color: var(--hms-h); }
.landing-section-sub { color: var(--hms-m); max-width:640px; }
.landing-eyebrow { background: var(--hms-primary-lt); color: var(--hms-primary); font-size:12px; letter-spacing:1.5px; }

/* ---- FEATURE CARDS ---- */
.landing-feature-card { background: var(--hms-bg1) !important; border:1px solid var(--hms-br) !important; border-radius:16px !important; transition:all .3s; }
.landing-feature-card:hover { border-color: var(--hms-primary) !important; transform: translateY(-4px); box-shadow:0 12px 40px var(--hms-primary-lt); }
.landing-feature-icon { width:52px; height:52px; border-radius:12px; display:flex; align-items:center; justify-content:center; }
.landing-feature-title { color: var(--hms-h); font-size:17px; }
.landing-feature-desc { color: var(--hms-m); font-size:14px; line-height:1.6; }
.landing-feature-tag { background: var(--hms-primary-lt); color: var(--hms-primary); padding:3px 10px; border-radius:50px; font-size:11px; font-weight:600; margin-right:6px; margin-bottom:4px; display:inline-block; }
.landing-feature-card-link { cursor:pointer; }
.landing-feature-card-link:focus-visible { outline:2px solid var(--hms-primary); outline-offset:2px; }
.landing-feature-cta { color: var(--hms-primary); font-size:13px; font-weight:600; display:inline-flex; align-items:center; gap:4px; opacity:.85; transition:gap .2s, opacity .2s; }
.landing-feature-card-link:hover .landing-feature-cta { opacity:1; gap:8px; }

/* ---- STEP CARDS ---- */
.landing-step-card { background: var(--hms-bg1) !important; border:1px solid var(--hms-br) !important; border-radius:16px !important; position:relative; }
.landing-step-number { position:absolute; top:-14px; left:50%; transform:translateX(-50%); width:28px; height:28px; background: var(--hms-primary); color:#fff; border-radius:50%; font-size:13px; font-weight:800; display:flex; align-items:center; justify-content:center; }
.landing-step-icon { font-size:36px; color: var(--hms-primary); }
.landing-step-title { color: var(--hms-h); }
.landing-step-desc { color: var(--hms-m); font-size:13px; line-height:1.6; }
.landing-flow-title { color: var(--hms-primary); font-weight:700; }

/* ---- ECO CARDS ---- */
.landing-eco-card { background: var(--hms-bg1) !important; border:1px solid var(--hms-br) !important; border-radius:16px !important; transition:all .3s; }
.landing-eco-card:hover { border-color: var(--hms-primary) !important; transform: translateY(-4px); }
.landing-eco-icon { font-size:40px; color: var(--hms-primary); margin-bottom:12px; }
.landing-eco-title { color: var(--hms-h); }
.landing-eco-desc { color: var(--hms-m); font-size:14px; line-height:1.6; }
.landing-eco-item { color: var(--hms-m); font-size:13px; gap:8px; }
.landing-eco-check { color: var(--hms-primary); }

/* ---- TRUST / SECURITY ---- */
.landing-trust-lead { font-size:16px; color: var(--hms-m); line-height:1.7; }
.landing-trust-list { gap:14px; }
.landing-trust-item { gap:12px; font-size:15px; color: var(--hms-h); }
.landing-trust-icon { color: var(--hms-primary); }
.landing-trust-card { background: var(--hms-bg1) !important; border:1px solid var(--hms-br) !important; border-radius:20px !important; }
.landing-flow-icon { width:60px; height:60px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:24px; background: var(--hms-primary-lt); color: var(--hms-primary); }
.landing-flow-mid { width:70px; height:70px; font-size:28px; }
.landing-flow-line { width:60px; height:2px; background: var(--hms-br); }
.landing-flow-labels { font-size:13px; font-weight:600; color: var(--hms-m); }
.landing-tl-list { gap:12px; }
.landing-tl-row { gap:12px; font-size:14px; color: var(--hms-m); }
.landing-tl-dot { width:12px; height:12px; border-radius:50%; border:2px solid var(--hms-br); flex-shrink:0; }
.landing-tl-dot.active { background: var(--hms-primary); border-color: var(--hms-primary); }

/* ---- PRICING ---- */
.landing-pricing-card { background: var(--hms-bg1) !important; border:1px solid var(--hms-br) !important; border-radius:20px !important; position:relative; transition:all .3s; }
.landing-pricing-card:hover { border-color: var(--hms-primary) !important; }
.landing-pricing-popular { border-color: var(--hms-primary) !important; }
.landing-popular-badge { position:absolute; top:-12px; left:50%; transform:translateX(-50%); background: var(--hms-primary); color:#fff; padding:4px 16px; border-radius:50px; font-size:11px; font-weight:800; letter-spacing:1px; white-space:nowrap; }
.landing-plan-tier { color: var(--hms-m); font-size:16px; }
.landing-plan-price { color: var(--hms-h); font-size:32px; }
.landing-plan-period { color: var(--hms-s); font-size:14px; font-weight:400; }
.landing-plan-row { color: var(--hms-h); font-size:14px; gap:8px; border-bottom:1px solid var(--hms-br); }
.landing-plan-row-off { color: var(--hms-s); opacity:.75; }
.landing-plan-icon-on { color: var(--hms-primary); }
.landing-plan-icon-off { color: var(--hms-s); }
.landing-plan-note { color: var(--hms-s); font-size:13px; }

/* ---- CTA ---- */
.landing-cta { background: var(--hms-bg2); border-top:1px solid var(--hms-br); }
.landing-cta-sub { font-size:18px; color: var(--hms-m); }
.landing-cta-note { color: var(--hms-s); font-size:13px; }

/* ---- FOOTER ---- */
.landing-footer { background: var(--hms-bg1); padding:60px 0 0; border-top:1px solid var(--hms-br); }
.landing-footer-logo { height:48px; }
.landing-footer-desc { font-size:14px; color: var(--hms-m); line-height:1.7; max-width:340px; }
.landing-footer-social { gap:12px; margin-top:16px; }
.landing-footer-heading { font-weight:700; margin-bottom:14px; text-transform:uppercase; font-size:13px; color: var(--hms-h); letter-spacing:.5px; }
.landing-footer-link { color: var(--hms-m); text-decoration:none; font-size:14px; cursor:pointer; }
.landing-footer-link:hover { color: var(--hms-primary); }
.landing-footer-bottom { border-top:1px solid var(--hms-br); }
.landing-footer-copy { color: var(--hms-s); font-size:13px; }
.landing-social-icon { width:36px; height:36px; background: var(--hms-bg2); border:1px solid var(--hms-br); border-radius:8px; display:flex; align-items:center; justify-content:center; color: var(--hms-s); font-size:18px; text-decoration:none; }
.landing-social-icon:hover { border-color: var(--hms-primary); color: var(--hms-primary); }

/* ---- THEME TOGGLE ---- */
.lp-theme-toggle {
  position:fixed; top:12px; right:16px; z-index:1060;
  width:40px; height:40px; border-radius:50%;
  background: var(--hms-bg3); border:1px solid var(--hms-br);
  color: var(--hms-primary); cursor:pointer; transition:all .3s;
  display:flex; align-items:center; justify-content:center;
  box-shadow:0 2px 8px rgba(0,0,0,.10);
}
.lp-theme-toggle:hover { transform: scale(1.1); box-shadow:0 4px 12px var(--hms-primary-lt); }
.lp-theme-toggle-mobile {
  width:38px; height:38px; border-radius:50%;
  background: var(--hms-bg3); border:1px solid var(--hms-br);
  color: var(--hms-primary); cursor:pointer; transition:all .2s;
  display:flex; align-items:center; justify-content:center; flex-shrink:0;
}
@media(max-width:991.98px){ .lp-theme-toggle { display:none; } }

/* ---- RESPONSIVE ---- */
@media(max-width:991px){ .landing-hero-title{font-size:38px} }
@media(max-width:575px){ .landing-hero-title{font-size:30px} .landing-hero-subtitle{font-size:15px} .landing-band-row{gap:24px} }
</style>